import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowRightIcon } from "lucide-react";
import {
  PODIUM_COUNT,
  TOP_VIDEOS_PAGE_SIZE,
  type VariantPreview,
} from "@scandi/shared";
import { CreativeThumb } from "@/components/creative-thumb";
import { cn } from "@/lib/utils";
import { rangeToParams, type RangeSelection } from "@/lib/api";
import type { Translate } from "@/i18n/translate";

/**
 * The way into the full ranking, sitting directly under the podium.
 *
 * It shows the videos rather than describing them: a fanned stack of the next
 * few thumbnails is an argument for clicking, where "see all" on its own is
 * just navigation. The whole strip is the link — the thumbnails are decoration
 * inside it, not separate destinations, so there is one target to hit and no
 * question about where a click lands.
 */
export function MoreVideos({
  next,
  total,
  range,
}: {
  next: VariantPreview[];
  /** Every variant that delivered in the range. */
  total: number;
  range: RangeSelection;
}) {
  const { t } = useTranslation();

  // Nothing to tease when the podium already showed everything there is.
  if (next.length === 0) return null;

  // What the ranking opens on, not how many variants delivered: promising
  // forty-seven and landing on a page of ten is a small lie the reader finds
  // out about one click later. More are a button away once they are there.
  const ranked = Math.max(
    Math.min(total, TOP_VIDEOS_PAGE_SIZE),
    PODIUM_COUNT + next.length,
  );
  const names = [...new Set(next.map((v) => v.editor))];

  return (
    <Link
      to={`/videos?${rangeToParams(range)}`}
      className={cn(
        "group bg-card hover:bg-accent/40 focus-visible:ring-ring/50 flex items-center gap-3",
        "rounded-xl border px-3 py-2.5 transition-colors outline-none focus-visible:ring-2",
      )}
    >
      {/* Overlapping, so five previews cost the width of about three and read
          as a queue with more behind it rather than a tidy row of five. The
          hover lift is a transform: spreading them would reflow the copy. */}
      <div className="flex shrink-0 items-center">
        {next.map((variant, i) => (
          <span
            key={variant.key}
            className={cn(
              "ring-card relative w-8 rounded ring-2 transition-transform duration-200",
              "group-hover:-translate-y-0.5",
              i > 0 && "-ml-3.5",
            )}
            style={{ zIndex: next.length - i }}
          >
            <CreativeThumb link={variant.link} width={160} noLink />
          </span>
        ))}
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-semibold">
          {t("topVariants.seeTop", { count: ranked })}
        </span>
        <span className="text-muted-foreground truncate text-xs">
          {names.length > 0
            ? t("topVariants.nextUpFrom", { names: listNames(names, t) })
            : t("topVariants.fullRanking")}
        </span>
      </div>

      <span className="text-muted-foreground group-hover:text-foreground flex shrink-0 items-center gap-1 text-xs font-semibold transition-colors">
        <span className="hidden sm:inline">{t("common.seeAll")}</span>
        <ArrowRightIcon className="size-4 transition-transform group-hover:translate-x-0.5" />
      </span>
    </Link>
  );
}

/** "Cristian and Laura", "Cristian, Laura and 2 others". */
function listNames(names: string[], t: Translate): string {
  if (names.length <= 2) return names.join(` ${t("common.listAnd")} `);
  return t("topVariants.andOthers", {
    names: `${names[0]}, ${names[1]}`,
    count: names.length - 2,
  });
}
